import React, { Component } from 'react'
import { connect } from 'react-redux'
import classnames from 'classnames'
import throttle from 'lodash.throttle'
import {
  updateCurrentRow
} from '../background-canvas/actions'
import MediaAsset from '../media-asset'
import AppLink from '../app-link'
import './collection-media-item.css'

import {
  getImageWidth,
  getImageHeight,
  getAspectRatio,
  getPositionStyle,
  getAspectRatioStyle } from '../../lib/gridUtil'

const getVideoHeight = (vw, colSpan, w, h) => {
  const imageW = getImageWidth(vw, colSpan)
  const ratio = getAspectRatio(w, h)
  const imageH = getImageHeight(imageW, ratio)  
  return imageH
}

const getOffsetStyle = (index, offset) => (
  {
    transform: `translate(0, ${offset}px)`
  }
)

const getActiveLink =  (id, slug, match) => {
  const url = match ? match.url : '/'
  const isArtist = url.indexOf('artists') !== -1
  const isHome = url === '/'
  const isCollection = url.indexOf('collection') !== -1
  return !isCollection && (isArtist || isHome)
}

const getHref = (id, slug, match) => {
  const url = match ? match.url : '/'
  const hrefSlug = slug || id
  const hrefPostType = 'collections'
  let href = `${hrefPostType}/${hrefSlug}`
  if (url.substr(-1) !== '/') {
    href = `${url}/${href}`
  }
  return href
}

const getLightboxHref = (match, lightboxIndex) => {
  const url = match.url
  let href = ''
  if (url.substr(-1) !== '/') {
    href = `${url}/${lightboxIndex}`
  } else {
    href = `${url}${lightboxIndex}`
  }
  return href
}

class CollectionMediaItem extends Component {
  constructor(props) {
    super(props)

    // bounding box 
    this.y0 = 0
    this.y1 = 0
    
    // offset properties
    this.tempOffset = 0
    this.targetOffset = 0
    this.renderOffset = 0

    // last check for mid point 
    this.lastBaseY = 0

    this.state = {
      inViewport: null,
      offset: 0
    }
  }

  componentDidMount() {
    const { st, vh } = this.props
    this.getRect()
    this.updatePosition()
    this.getMomentumOffset(st, vh, this.y0, this.y1, true)
  }
  
  componentWillReceiveProps(props) {
    if (props.st !== this.props.st) {
      this.updatePosition()
    }
  }

  componentDidUpdate() {
    this.getRect()
  }

  getRect() {
    const { st } = this.props 
    const rect = this.el.getBoundingClientRect()

    const y0 = rect.top + st
    const y1 = y0 + rect.height

    // y0 represents y position of element top in document
    // ie. if at 0, always will be a 0 wherever you scroll
    this.y0 = y0
    // y1 represent y position of element bottom 
    this.y1 = y1
  }

  updatePosition = throttle(() => {
    const { st, vh, index, rowIndex } = this.props

    // element above
    const bufferHeight = -300
    const elementAbove = this.y1 < st + bufferHeight
    const elementBelow = this.y0 > (st + vh) - bufferHeight

    const inViewport = !elementAbove && !elementBelow

    this.setState(state => {
      if (state.inViewport === inViewport) { return state }
      return { inViewport }
    })
    
    if (index === 0) {
      this.props.onUpdateCurrentRow(rowIndex, this.y0 - window.innerHeight * 0.65, st)
    }

    if (inViewport && !!index) {
      this.getMomentumOffset(st, vh, this.y0, this.y1)      
    }

  }, 250)

  getSign(x) {
    return x > 0 ? 1 : x < 0 ? -1 : 0
  }

  getMomentumOffset(st, vh, y0, y1, immediate) {
    const { index, colSpan } = this.props

    // calculate distance from centre of screen
    // const elementHeight = y1 - y0
    const elementY = y0 // + elementHeight * 0.5
    const viewportBaseY = st + vh

    if (this.lastBaseY !== viewportBaseY) {
      // distance from centre 
      const delta = viewportBaseY - elementY
      // 0 to 1 value of distance from centre to viewport centre
      const deltaRatio = delta / vh
      const deltaRatioClamp = Math.max(-1, Math.min(1, deltaRatio))
      // amount to offset 
      const offset = deltaRatioClamp * 60 * index * (1 - (colSpan / 24)) * -1
      const limitedOffset = Math.min(offset, 100)
      // set target if changed 
      if (limitedOffset !== this.targetOffset) {
        this.targetOffset = limitedOffset
      }
      // save value 
      this.lastBaseY = viewportBaseY
    }

    if (immediate) {
      this.tempOffset = this.targetOffset
    } else {
      this.tempOffset += (this.targetOffset - this.tempOffset) * 0.75
    }

    const renderOffset = this.tempOffset.toFixed(2)

    if (renderOffset !== this.renderOffset) {
      this.setState({
        offset: renderOffset
      })
      this.renderOffset = renderOffset
    }
  }

  shouldComponentUpdate(nextProps, nextState) {
    if (nextProps.index !== this.props.index) return true
    if (nextProps.vw !== this.props.vw) return true
    if (nextProps.vh !== this.props.vh) return true
    if (nextProps.xOffset !== this.props.xOffset) return true
    if (nextProps.yOffset !== this.props.yOffset) return true
    if (nextProps.colSpan !== this.props.colSpan) return true
    if (nextProps.imageWidth !== this.props.imageWidth) return true
    if (nextProps.imageHeight !== this.props.imageHeight) return true
    if (nextProps.videoSrc !== this.props.videoSrc) return true
    if (nextProps.imageSrc !== this.props.imageSrc) return true
    if (nextProps.match !== this.props.match) return true
    
    const { inViewport: nextInViewport } = nextState
    const { inViewport: prevInViewport } = this.state
    if (nextInViewport !== prevInViewport) return true
  
    if (nextState.offset !== this.state.offset) return true
    return false
  }

  renderMedia() {
    const { 
      index,
      vw,
      vh,
      xOffset,
      yOffset,
      colSpan,
      imageWidth,
      imageHeight,
      onImageEnter,
      onImageLeave,
      videoSrc,
      videoPosterSrc,
      imageSrc
    } = this.props 

    const {
      offset
    } = this.state

    const itemClassNames = classnames({
      'collection__media-item-aside': index > 0,
      'collection__media-item--in-viewport': this.state.inViewport
    }, 'collection__media-item')
    
    return (
      <div 
        style={getOffsetStyle(index, offset, colSpan)} 
        className={itemClassNames}>
        <div 
          ref={el => this.el = el}
          onMouseEnter={onImageEnter}
          onMouseLeave={onImageLeave}
          style={getPositionStyle(vw, xOffset, yOffset, colSpan)} 
          className="collection__media-position">
          <div 
            style={getAspectRatioStyle(vw, colSpan, getAspectRatio(imageWidth, imageHeight))} 
            className="collection__media-ratio">
            <MediaAsset 
              style={getAspectRatioStyle(vw, colSpan, getAspectRatio(imageWidth, imageHeight))}
              index={index}
              classNames="collection__media-image" 
              vh={vh}
              active={this.props.match && this.props.match.isExact && !!this.state.inViewport}
              usePlayer={this.props.usePlayer}
              videoSrc={videoSrc}
              videoPosterSrc={videoPosterSrc}
              videoWidth={getImageWidth(vw, colSpan)}
              videoHeight={getVideoHeight(vw, colSpan, imageWidth, imageHeight)}
              src={imageSrc} />
          </div>
        </div>
      </div>
    )
  }

  render() {
    const {
      id,
      slug,
      match,
      lightboxIndex
    } = this.props 

    const isPageLink = getActiveLink(id, slug, match)
    
    return this.props.usePlayer 
      ? this.renderMedia() 
      : (
        isPageLink  
          ? <AppLink to={getHref(id, slug, match)}>{this.renderMedia()}</AppLink>
          : <AppLink to={getLightboxHref(match, lightboxIndex)}>{this.renderMedia()}</AppLink>
      )
  }
}

const mapDispatchToProps = dispatch => {
  return {
    onUpdateCurrentRow(index, delta, st) {
      dispatch(updateCurrentRow(index, delta, st))
    }
  }
}

export default connect(null, mapDispatchToProps)(CollectionMediaItem)


// WEBPACK FOOTER //
// ./src/components/collection-media/collection-item.js